import { Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { IRequestOptions } from "@core/interfaces/api-request-options.interface";
import { ApiService } from "@core/services/api.service";
import { ApiServiceHolder } from "@core/utils/api-service-holder";
import { UntilDestroy } from "@ngneat/until-destroy";
import { IKeyValue } from "@shared/interfaces/key-value.interface";
import { FilterProperty, FilterPropertyValue, FilterStatus, GetAllFilterPropertiesDto, IAuctionFilterDto } from "@shared/interfaces/search.interface";
import { Observable, Subject, of, tap, throwError } from "rxjs";
import { ArticleProperty, AuctionView } from "src/app/articles-manager/interfaces/auction.interface";

const cache: {[key: string]: {
  time: Date;
  value: {count: number, auctions: AuctionView[]};
}} = {}

@Injectable({
  providedIn: 'root'
})
@UntilDestroy()
export class SearchService {
  auctionTypeList: IKeyValue[] = [];
  sportList: IKeyValue[] = [];
  competitionList: IKeyValue[] = [];
  teamList: IKeyValue[] = [];
  filtersChange = new Subject<{}>();
  lastResult: AuctionView[] = [];
  lastSearch: Date;

  lastResultByType: {
    [key: string]: {
      [key: number]: {
        lastResult: AuctionView[];
        lastSearch: Date;
        lastFilters: IAuctionFilterDto;
      }
    },
  } = {};
  filters: IAuctionFilterDto = {
    idLanguage: -1,
    searchText: '',
    idAuctionType: -1,
    idDetail: null,
    onlySigned: false,
    idSport: null,
    idCompetition: null,
    idTeam: null,
    onlyNew: false,
    onlyExpiring: false,
    onlyArriving: false,
    auctionStatus: FilterStatus.OnlyActive,
    idEntity: null,
    idCampaign: null,
    idPromoter: null,
    idActiveAuction: null,
    idTagList: [],
    orderBy: 'evidence',
    orderDirection: 'desc'
  };
  searchRoute: string = '';

  constructor(
    private _api: ApiService,
    private _router: Router,
    private _route: ActivatedRoute
  ) { }

  setFilters(nextRoute: string, filters: IAuctionFilterDto) {
    this.filters = filters;
    if (this.searchRoute == nextRoute) {
      this.filtersChange.next({});
    } else {
      this.searchRoute = nextRoute;
      this._router.navigate([this.searchRoute], { relativeTo: this._route });
    }
  }

  listenFiltersChanges(): Observable<any> {
    return this.filtersChange.asObservable();
  }

  private deepEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) {
      return true;
    }
    if (obj1 == null || obj2 == null) {
      return false;
    }
    if (typeof obj1 != 'object' || typeof obj2 != 'object') {
      return false;
    }
    if (Object.keys(obj1).length != Object.keys(obj2).length) {
      return false;
    }
    for (let key in obj1) {
      if (!obj2.hasOwnProperty(key)) {
        return false;
      }
      if (!this.deepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }
    return true;
  }

  getAll(filters: IAuctionFilterDto): Observable<AuctionView[]> {
    return this._api.request<AuctionView[]>(ApiServiceHolder.auctionView.getAll(filters))
  }


  getAllAndCount(filters: IAuctionFilterDto): Observable<{count: number, auctions: AuctionView[]}> {
    const key = JSON.stringify(filters);
    const now = new Date();
    if (cache[key] && now.getTime() - cache[key].time.getTime() < 60000) {
      return of(cache[key].value);
    }

    const res = this._api.request<{count: number, auctions: AuctionView[]}>(ApiServiceHolder.auctionView.getAllAndCount(filters))
    .pipe(
      tap((res) => {
        cache[key] = {
          time: now,
          value: res
        }
      })
    )
    ;


    return res;

  }

  getAllForUser(idLanguage: number, userAuctionFilter: 'onlystarred' | 'onlywin' | 'onlylost'): Observable<AuctionView[]> {
    let apiOptions: IRequestOptions;
    switch (userAuctionFilter) {
      case 'onlystarred':
        apiOptions = ApiServiceHolder.auction.getAllForUserStarred(idLanguage);
        break;
      case 'onlywin':
        apiOptions = ApiServiceHolder.auction.getAllForUserWin(idLanguage);
        break;
      case 'onlylost':
        apiOptions = ApiServiceHolder.auction.getAllForUserLost(idLanguage);
        break;
      default:
        throwError(() => 'No type selected.');
        break;
    }
    return this._api.request<AuctionView[]>(apiOptions);
  }

  getAllAuction(idLanguage: number): Observable<AuctionView[]> {
    return this._api.request(ApiServiceHolder.auction.getAll(idLanguage));
  }

  getAllTypes(): Observable<IKeyValue[]> {
    return this._api.request(ApiServiceHolder.filter.getAllTypes());
  }

  getAllFilterProperties(idArticleType: number): Observable<FilterProperty[]> {
    return this._api.request(ApiServiceHolder.filter.getAllFilterProperties(idArticleType));
  }

  getFilterPropertyValue(itemOrder: number, value: string): Observable<FilterPropertyValue[]> {
    return this._api.request(ApiServiceHolder.filter.getFilterPropertyValue(itemOrder, value));
  }

  getAllProperties(filterProperties: GetAllFilterPropertiesDto): Observable<ArticleProperty[]> {
    return this._api.request(ApiServiceHolder.filter.getAllProperties(filterProperties));
  }
}
